"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VerificationFailuresBatch = exports.VerificationFailure = exports.PlaybackEventsBatch = exports.PlaybackEvent = exports.HeartbeatInput = exports.MaintenanceAssignment = exports.PlayerAuditEntry = exports.SetGpsBody = exports.SetLifecycleBody = exports.PlayerSummary = exports.PlayerGps = exports.ScreenStatuses = exports.PlayerStatus = exports.PlayerClassOverride = exports.HardwareProfileDelta = exports.HardwareProfile = void 0;
const zod_1 = require("zod");
const enums_js_1 = require("./enums.js");
exports.HardwareProfile = zod_1.z.object({
    user_agent: zod_1.z.string().max(512).optional(),
    screen_width: zod_1.z.number().int().positive().optional(),
    screen_height: zod_1.z.number().int().positive().optional(),
    device_pixel_ratio: zod_1.z.number().positive().optional(),
    locale: zod_1.z.string().max(16).optional(),
    timezone: zod_1.z.string().max(64).optional(),
    storage_quota_bytes: zod_1.z.number().int().nonnegative().optional(),
    player_class: zod_1.z.enum(enums_js_1.PlayerClasses).optional(),
    capability_flags: zod_1.z.array(zod_1.z.string()).optional(),
});
exports.HardwareProfileDelta = zod_1.z
    .object({
    capability_flags: zod_1.z.array(zod_1.z.string()).optional(),
    player_class: zod_1.z.enum(enums_js_1.PlayerClasses).optional(),
})
    .strict();
exports.PlayerClassOverride = zod_1.z
    .object({
    player_class: zod_1.z.enum(enums_js_1.PlayerClasses).optional(),
    capability_flags: zod_1.z.array(zod_1.z.string()).optional(),
    reason: zod_1.z.string().min(3),
})
    .refine((v) => v.player_class !== undefined || v.capability_flags !== undefined, { message: 'nothing to override' });
exports.PlayerStatus = zod_1.z.object({
    current_resource_version_id: zod_1.z.string().uuid().nullable(),
    current_program_id: zod_1.z.string().uuid().nullable(),
    current_item_index: zod_1.z.number().int().nonnegative().nullable(),
    online_since: zod_1.z.string().datetime().nullable(),
    storage_used_bytes: zod_1.z.number().int().nonnegative().nullable(),
});
// Screen-inventory (SP2): the resolved display status the inventory shows —
// the lifecycle badge when not 'active', else the live operational state.
exports.ScreenStatuses = [
    'running',
    'idle',
    'down',
    'maintenance',
    'broken',
    'end_of_life',
];
exports.PlayerGps = zod_1.z.object({
    lat: zod_1.z.number(),
    lng: zod_1.z.number(),
    source: zod_1.z.enum(['device', 'manual']),
});
exports.PlayerSummary = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    group_id: zod_1.z.string().uuid().nullable(),
    display_name: zod_1.z.string(),
    pairing_state: zod_1.z.enum(enums_js_1.PairingStates),
    last_seen_at: zod_1.z.string().datetime().nullable(),
    last_status: exports.PlayerStatus.nullable(),
    hardware_profile: exports.HardwareProfile.nullable(),
    player_class: zod_1.z.enum(enums_js_1.PlayerClasses).nullable(),
    // Screen-inventory (SP2) — OPTIONAL during the rollout: the server always
    // provides them (players.service.toSummary), but they are optional so the
    // existing admin consumers/tests (which predate SP2) keep compiling. Phase B
    // (the inventory UI) consumes them.
    device_type: zod_1.z.enum(enums_js_1.DeviceTypes).optional(),
    lifecycle_status: zod_1.z.enum(enums_js_1.LifecycleStatuses).optional(),
    current_composer_id: zod_1.z.string().uuid().nullable().optional(),
    screen_on: zod_1.z.boolean().optional(),
    gps: exports.PlayerGps.nullable().optional(),
    location_path: zod_1.z.array(zod_1.z.string()).optional(), // resolved ancestry, building→room
    status: zod_1.z.enum(exports.ScreenStatuses).optional(), // resolved display status
    created_at: zod_1.z.string().datetime(),
});
// Screen-inventory (SP2): admin request bodies.
exports.SetLifecycleBody = zod_1.z.object({
    status: zod_1.z.enum(enums_js_1.LifecycleStatuses),
    reason: zod_1.z.string().min(1).max(500).optional(),
});
exports.SetGpsBody = zod_1.z.object({
    lat: zod_1.z.number().min(-90).max(90).nullable(),
    lng: zod_1.z.number().min(-180).max(180).nullable(),
});
// SP2 fast-follow: one entry in a screen's lifecycle history (maintenance /
// broken / end-of-life / return-to-service), reconstructed from the append-only
// AuditEvent rows that setLifecycle writes.
exports.PlayerAuditEntry = zod_1.z.object({
    from: zod_1.z.enum(enums_js_1.LifecycleStatuses),
    to: zod_1.z.enum(enums_js_1.LifecycleStatuses),
    reason: zod_1.z.string().nullable(),
    at: zod_1.z.string(),
    actor_id: zod_1.z.string(),
});
// Screen-inventory (SP2): the /me/assignment response when a screen is PAUSED
// (lifecycle maintenance/broken/end_of_life). The player renders its
// maintenance screen instead of content. Additive — existing assignment
// consumers branch on the `maintenance` discriminator.
exports.MaintenanceAssignment = zod_1.z.object({ maintenance: zod_1.z.literal(true) });
exports.HeartbeatInput = zod_1.z.object({
    // P5 W1: append 'paused' (additive). Existing consumers ignore unknown
    // states via z.enum; old clients that emit one of the 4 prior states
    // continue to work unchanged.
    state: zod_1.z.enum(['playing', 'playing_offline', 'loading_assets', 'error', 'paused']),
    status: exports.PlayerStatus,
    // Screen-inventory (SP2, PWA path): best-effort self-reported GPS riding the
    // heartbeat, mirroring the fields the Android wrapper sends in
    // DeviceTelemetry (device-control.schema.ts). Optional + nullable so old
    // players (which omit them) keep parsing; the server stores them only when
    // no manual admin pin exists. accuracy in metres.
    gps_lat: zod_1.z.number().min(-90).max(90).nullable().optional(),
    gps_lng: zod_1.z.number().min(-180).max(180).nullable().optional(),
    gps_accuracy: zod_1.z.number().nonnegative().nullable().optional(),
});
exports.PlaybackEvent = zod_1.z.object({
    ts: zod_1.z.string().datetime(),
    type: zod_1.z.enum([
        'item.start',
        'item.end',
        'composer.cycle',
        'verification.failed',
        'asset.fetch.failed',
    ]),
    resource_version_id: zod_1.z.string().uuid().optional(),
    program_id: zod_1.z.string().uuid().optional(),
    detail: zod_1.z.record(zod_1.z.unknown()).optional(),
});
exports.PlaybackEventsBatch = zod_1.z.object({
    events: zod_1.z.array(exports.PlaybackEvent).min(1).max(200),
});
exports.VerificationFailure = zod_1.z.object({
    resource_version_id: zod_1.z.string().uuid(),
    reason: zod_1.z.enum([
        'signature_invalid',
        'sha256_mismatch',
        'expired',
        'revoked',
        'unknown_signing_key',
        'manifest_malformed',
    ]),
    ts: zod_1.z.string().datetime(),
});
exports.VerificationFailuresBatch = zod_1.z.object({
    failures: zod_1.z.array(exports.VerificationFailure).min(1).max(50),
});
//# sourceMappingURL=player.schema.js.map